import { z } from 'zod';
import { ErrorResponseSchema } from './errors';
import { StorySpreadSchema } from './ai';
// Age range enum
export const AgeRangeSchema = z.enum(['0-3', '3-6', '6-9', '9-12', '12-18']);
// Story themes enum
export const StoryThemesSchema = z.enum([
    'family',
    'adventure',
    'special_memories',
    'milestones',
    'new_baby',
    'new_sibling',
    'holidays',
    'bedtime',
    'educational',
    'love',
    'custom',
]);
export const StoryTemplateRoleSchema = z.object({
    id: z.string(),
    title: z.string(),
    description: z.string(),
});
// Schema for raw database response with nested relationships this is only used on the BE to validate the database response before transforming it for the FE
export const StoryTemplateDatabaseResponseSchema = z.object({
    id: z.string(),
    title: z.string(),
    description: z.string().nullable(),
    age_range: AgeRangeSchema,
    story_themes: z.array(StoryThemesSchema),
    key_events: z.array(z.string()).nullable(),
    setting_details: z.string().nullable(),
    spread_count: z.number().int().min(1).max(20).nullable(),
    cover_image_prompt: z.string().nullable(),
    spreads: z.array(StorySpreadSchema).nullable(),
    style_template_id: z.string(),
    story_template_roles: z.array(z.object({
        id: z.string(),
        deleted_at: z.string().nullable(),
        role_templates: z.object({
            // supabase automatically uses the referenced table name as the column name in joins,
            // hence plural name of role_templates despite only returning one role_template per story_template_role
            id: z.string(),
            title: z.string(),
            description: z.string(),
        }),
    })),
});
export const StoryTemplateSchema = z.object({
    id: z.string(),
    title: z.string(),
    description: z.string().optional(),
    ageRange: AgeRangeSchema,
    storyThemes: z.array(StoryThemesSchema),
    keyEvents: z.array(z.string()).optional(),
    settingDetails: z.string().optional(),
    spreadCount: z.number().int().min(1).max(20).optional(),
    coverImagePrompt: z.string().optional(),
    spreads: z.array(StorySpreadSchema).optional(),
    styleTemplateId: z.string(),
    story_template_roles: z
        .array(StoryTemplateRoleSchema)
        .min(1, 'Story template must have at least one role'),
});
// Schema for story template with cover image URL (for frontend display)
export const StoryTemplateWithUrlsSchema = StoryTemplateSchema.extend({
    coverImageUrl: z.string().optional(),
});
// Success response schema for single template
export const StoryTemplateSuccessResponseSchema = z.object({
    type: z.literal('success'),
    data: StoryTemplateWithUrlsSchema,
});
// Success response schema for multiple templates
export const StoryTemplatesSuccessResponseSchema = z.object({
    type: z.literal('success'),
    data: z.array(StoryTemplateWithUrlsSchema),
});
// Union type for single template API responses
export const StoryTemplateResponseSchema = z.discriminatedUnion('type', [
    StoryTemplateSuccessResponseSchema,
    ErrorResponseSchema,
]);
// Union type for multiple templates API responses
export const StoryTemplatesResponseSchema = z.discriminatedUnion('type', [
    StoryTemplatesSuccessResponseSchema,
    ErrorResponseSchema,
]);
// ===== CREATE STORY TEMPLATE SCHEMAS =====
// Form schema for custom template creation (only fields shown in the form)
export const CreateCustomTemplateFormSchema = z.object({
    title: z.string().min(1, 'Title is required'),
    description: z.string().optional(),
    style_template_id: z.string().min(1, 'Style template is required'),
    role_template_ids: z
        .array(z.string().min(1, 'Role template ID cannot be empty'))
        .min(1, 'At least one role template is required'),
});
// Schema for creating a new story template (no id field)
export const CreateStoryTemplateRequestSchema = z.object({
    title: z.string().min(1, 'Title is required'),
    description: z.string().optional(),
    ageRange: AgeRangeSchema,
    storyThemes: z
        .array(StoryThemesSchema)
        .min(1, 'At least one story theme is required'),
    keyEvents: z.array(z.string()).optional(),
    settingDetails: z.string().optional(),
    style_template_id: z.string(),
    role_template_ids: z
        .array(z.string().min(1, 'Role template ID cannot be empty'))
        .min(1, 'At least one role template is required'),
});
// Success response for creating a story template
export const CreateStoryTemplateSuccessResponseSchema = z.object({
    type: z.literal('success'),
    data: z.object({
        message: z.string(),
        storyTemplate: StoryTemplateWithUrlsSchema,
    }),
});
// Union type for create story template API responses
export const CreateStoryTemplateResponseSchema = z.discriminatedUnion('type', [
    CreateStoryTemplateSuccessResponseSchema,
    ErrorResponseSchema,
]);
// ===== SOFT DELETE STORY TEMPLATE SCHEMAS =====
// Schema for validating delete request parameters
export const DeleteStoryTemplateRequestSchema = z.object({
    id: z.uuid({ message: 'Invalid story template ID format' }),
});
// Success response for soft deleting a story template (PUT operation)
export const SoftDeleteStoryTemplateSuccessResponseSchema = z.object({
    type: z.literal('success'),
    data: z.object({
        message: z.string(),
        deletedTemplateId: z.string(),
    }),
});
// Union type for soft delete story template API responses
export const SoftDeleteStoryTemplateResponseSchema = z.discriminatedUnion('type', [SoftDeleteStoryTemplateSuccessResponseSchema, ErrorResponseSchema]);
